// Please keep files in back/src/api/compute-field updated

import { SupportedLocale } from "@/i18n";
import { ComponentProps, ReactNode } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  ArtworkDescriptionEnsembleFixeCF,
  ArtworkDescriptionEnsembleTemporaireCf,
  ArtworkDimensionsCadreCF,
  ArtworkDimensionsCF,
  ArtworkDimensionsHorsMargesCF,
  ArtworkEstimationFromMonetaryTransactionsCF,
  ArtworkLocalisationHistoriqueCF,
  ArtworkMainTitleCF,
  ArtworkOverviewSummaryCF,
  ArtworkRelatedAuthorsCF,
  ArtworkSignatureCF,
  ArtworkSummaryCF,
  ArtworkSummaryPhotographieMasterCF,
  ArtworkSupportTechniqueCF,
  UnstyledArtworkTitleCF,
} from "./compute-field/artwork";
import { RelatedAuctionsCF } from "./compute-field/artwork-in-auction";
import { RelatedBibliographiesCF } from "./compute-field/artwork-in-bibliography";
import { RelatedExhibitionsCF } from "./compute-field/artwork-in-exhibition";
import { AuctionSummaryCF } from "./compute-field/auction";
import { AudiovisualSummaryCF } from "./compute-field/audiovisual";
import {
  BibliographieDimensionsCF,
  BibliographieLongSummaryCF,
} from "./compute-field/bibliography";
import {
  CollectorNameCF,
  CollectorNameForOverviewCF,
} from "./compute-field/collector";
import { decodeHtmlEntities } from "./utils";
import { DocumentSummaryCF } from "./compute-field/document";
import { ExhibitionSummaryCF } from "./compute-field/exposition";
import { RelatedBibliographiesMultipleprintsCF } from "./compute-field/multipleprint-in-bibliography";
import { RelatedBibliographiesMultiplesamplesCF } from "./compute-field/multiplesample-in-bibliography";
import { RelatedExhibitionsMultipleprintCF } from "./compute-field/multipleprint-in-exhibition";
import {
  MultipleprintDimensionImageCF,
  MultipleprintDimensionsCF,
  MultipleprintInscriptionDosCF,
  MultipleprintRelatedAuthorsCF,
  MultipleprintSignatureCF,
  MultipleprintSummaryCF,
  MultipleprintSummaryDimensionsCF,
  MultipleprintSupportTechniqueCF,
  MultipleprintTitleCF,
} from "./compute-field/multipleprint";
import {
  PhotographyDimensionsCF,
  PhotographySummaryCF,
} from "./compute-field/photographie";
import { DimensionsFormatting } from "./compute-field/shared/dimensions";
import { InscriptionDosCF } from "./compute-field/shared/inscription-dos";
import { DateCF } from "./compute-field/shared/title";
import {
  SculptureEditionHistoryCF,
  SculptureEditionSummaryCF,
} from "./compute-field/sculpture-edition";
import { TirageHistoryCF } from "./compute-field/tirage";
import { EcritSummaryCF } from "./compute-field/writing";
import type { SculptureEdition } from "./types";

export type ComputeFieldComponent<T, CustomProps = unknown> = (
  props: {
    input: T;
    lineBreak?: boolean;
    inline?: boolean;
    endWithPeriod?: boolean;
    language: SupportedLocale;
    defaultLanguage?: SupportedLocale;
    translation?: (key: string) => string;
  } & CustomProps,
) => ReactNode;

function computeFieldRenderer<T>(
  CFComponent: ComputeFieldComponent<T>,
): (
  input: T,
  params: Omit<ComponentProps<ComputeFieldComponent<T>>, "input">,
) => string {
  return (input: T, params) =>
    renderToStaticMarkup(CFComponent({ input, ...params }));
}

export function unwrapPTag(input: string) {
  return input.replace(/<p[^>]*>(.*?)<\/p>/g, "$1");
}

////////// Artwork
export const computeFieldUnstyledArtwork = computeFieldRenderer(
  UnstyledArtworkTitleCF,
);

export const computeFieldDimensionsArtwork =
  computeFieldRenderer(ArtworkDimensionsCF);

export const computeFieldTitleArtwork =
  computeFieldRenderer(ArtworkMainTitleCF);

export const computeFieldDimensionsHorsMargesArtwork = computeFieldRenderer(
  ArtworkDimensionsHorsMargesCF,
);

export const computeFieldDimensionsCadreArtwork = computeFieldRenderer(
  ArtworkDimensionsCadreCF,
);

export const computeFieldRelatedAuthorsArtwork = computeFieldRenderer(
  ArtworkRelatedAuthorsCF,
);

export const computeFieldSupportTechniqueArtwork = computeFieldRenderer(
  ArtworkSupportTechniqueCF,
);

export const computeFieldSignatureArtwork =
  computeFieldRenderer(ArtworkSignatureCF);

export const computeFieldInscriptionDosArtwork =
  computeFieldRenderer(InscriptionDosCF);

export const computeFieldSummaryArtwork =
  computeFieldRenderer(ArtworkSummaryCF);

export const computeFieldSummaryPhotographieMasterArtwork =
  computeFieldRenderer(ArtworkSummaryPhotographieMasterCF);

export const computeFieldArtworkOverviewSummary = <
  T extends ComponentProps<typeof ArtworkOverviewSummaryCF>["input"],
>(
  input: T,
  params: Omit<ComponentProps<typeof ArtworkOverviewSummaryCF>, "input">,
): string => {
  return renderToStaticMarkup(
    ArtworkOverviewSummaryCF({ input, ...params } as ComponentProps<
      typeof ArtworkOverviewSummaryCF
    >),
  );
};

export const computeFieldEstimationFromMonetaryTransactionsArtwork =
  computeFieldRenderer(ArtworkEstimationFromMonetaryTransactionsCF);

export const computeFieldDescriptionEnsembleFixeArtwork = computeFieldRenderer(
  ArtworkDescriptionEnsembleFixeCF,
);

export const computeFieldDescriptionEnsembleTemporaireArtwork =
  computeFieldRenderer(ArtworkDescriptionEnsembleTemporaireCf);

export const computeFieldLocalisationHistoriqueArtwork = computeFieldRenderer(
  ArtworkLocalisationHistoriqueCF,
);

export const computeFieldRelatedExhibitions =
  computeFieldRenderer(RelatedExhibitionsCF);

export const computeFieldRelatedAuctions =
  computeFieldRenderer(RelatedAuctionsCF);

export const computeFieldRelatedBibliographies = computeFieldRenderer(
  RelatedBibliographiesCF,
);

///////// Bibliography

export const computeFieldDimensionsBibliographie = computeFieldRenderer(
  BibliographieDimensionsCF,
);

export const computeFieldLongSummaryBibliographie = computeFieldRenderer(
  BibliographieLongSummaryCF,
);

///////// Collector

const _computeFieldCollectorNameRaw = computeFieldRenderer(CollectorNameCF);
const _computeFieldCollectorNameForOverviewRaw = computeFieldRenderer(
  CollectorNameForOverviewCF,
);

export const computeFieldCollectorName = (
  input: ComponentProps<typeof CollectorNameCF>["input"],
  params: Omit<ComponentProps<typeof CollectorNameCF>, "input">,
) => decodeHtmlEntities(_computeFieldCollectorNameRaw(input, params));

export const computeFieldCollectorNameForOverview = (
  input: ComponentProps<typeof CollectorNameForOverviewCF>["input"],
  params: Omit<ComponentProps<typeof CollectorNameForOverviewCF>, "input">,
) =>
  decodeHtmlEntities(_computeFieldCollectorNameForOverviewRaw(input, params));

///////// Auction

export const computeFieldAuctionSummary =
  computeFieldRenderer(AuctionSummaryCF);

///////// Multipleprint

export const computeFieldMultipleprintDimensions = computeFieldRenderer(
  MultipleprintDimensionsCF,
);

export const computeFieldMultipleprintDimensionImage = computeFieldRenderer(
  MultipleprintDimensionImageCF,
);

export const computeFieldMultipleprintSummaryDimensions = computeFieldRenderer(
  MultipleprintSummaryDimensionsCF,
);

export const computeFieldMultipleprintRelatedAuthors = computeFieldRenderer(
  MultipleprintRelatedAuthorsCF,
);

export const computeFieldMultipleprintSupportTechnique = computeFieldRenderer(
  MultipleprintSupportTechniqueCF,
);

export const computeFieldMultipleprintSignature = computeFieldRenderer(
  MultipleprintSignatureCF,
);

export const computeFieldMultipleprintInscriptionDos = computeFieldRenderer(
  MultipleprintInscriptionDosCF,
);

export const computeFieldMultipleprintTitle =
  computeFieldRenderer(MultipleprintTitleCF);

export const computeFieldMultipleprintSummary = computeFieldRenderer(
  MultipleprintSummaryCF,
);

export const computeFieldRelatedExhibitionsMultipleprint =
  computeFieldRenderer(RelatedExhibitionsMultipleprintCF);

export const computeFieldRelatedBibliographiesMultipleprints =
  computeFieldRenderer(RelatedBibliographiesMultipleprintsCF);

export const computeFieldRelatedBibliographiesMultiplesamples =
  computeFieldRenderer(RelatedBibliographiesMultiplesamplesCF);

///////// Writing

export const computeFieldWritingDescription =
  computeFieldRenderer(EcritSummaryCF);

///////// Audiovisual

export const computeFieldAudiovisualSummary =
  computeFieldRenderer(AudiovisualSummaryCF);

///////// Document

export const computeFieldDocumentSummary =
  computeFieldRenderer(DocumentSummaryCF);

///////// Exhibition

export const computeFieldExhibitionSummary =
  computeFieldRenderer(ExhibitionSummaryCF);

///////// Photography

export const computeFieldPhotographySummary =
  computeFieldRenderer(PhotographySummaryCF);

export const computeFieldPhotographyDimensions = computeFieldRenderer(
  PhotographyDimensionsCF,
);

export const computeFieldDate = computeFieldRenderer(DateCF);

///////// Tirage (prints)

export const computeFieldTirageDimensions =
  computeFieldRenderer(DimensionsFormatting);

export const computeFieldTirageHistory = computeFieldRenderer(TirageHistoryCF);

export const computeFieldSculptureEditionHistory = computeFieldRenderer(
  SculptureEditionHistoryCF,
);

export const computeFieldSculptureEditionSummary = computeFieldRenderer(
  SculptureEditionSummaryCF,
);

/** Plain-text subtitle for sculpture edition link tables (replaces multipleprint authors column). */
export function pickSculptureEditionTechniqueLine(
  edition: SculptureEdition,
  opts: { language: SupportedLocale },
): string {
  const tech = edition.sculptureEditionTechnique;
  if (!tech || typeof tech !== "object") return "";
  const v =
    tech[opts.language] ??
    tech.fr ??
    tech.en ??
    tech.es ??
    tech.nl ??
    "";
  return decodeHtmlEntities(String(v).replace(/<[^>]+>/g, "")).trim();
}
